import { get, noop, reduce } from 'lodash';
import { ABServiceClient } from '@zocdoc/ab';
import { ActionTypes, OPTIMIZELY_PROJECT } from './constants';
import * as experiments from './experiments';
import { sanitizeId } from './util';
import { __DEV__ } from '@zocdoc/webpack-recipes/lib/env';
import { isProduction } from '../../utils/environment';

const ENDPOINT = process.env.INTERNAL_AWS_API_HOST || process.env.AWS_API_HOST;

const experimentsKeyedById = reduce(experiments, (result, value) => {
    result[value.experimentId] = value;
    return result;
}, {});

function generateABClient(options) {
    return new ABServiceClient({
        basePath: ENDPOINT,
        fetch: options.fetch,
        timeoutInMillis: 1000,
    });
}

export function loadingAssignments(options) {
    return {
        type: ActionTypes.LOADING_ASSIGNMENTS,
        payload: options,
    };
}

export function receivedAssignments(assignments) {
    return {
        type: ActionTypes.RECEIVED_ASSIGNMENTS,
        payload: assignments,
    };
}

export function recordedConversion(conversionId) {
    return {
        type: ActionTypes.TRACKED_CONVERSION,
        conversionId,
    };
}

export function assignmentsError(error) {
    return {
        type: ActionTypes.ASSIGNMENTS_ERROR,
        payload: error,
    };
}
export function conversionError(error) {
    return {
        type: ActionTypes.CONVERSION_ERROR,
        payload: error,
    };
}
export function fetchAssignments({
    fetch = fetchWithRetries,
    log = noop,
    sessionId,
    url,
    userAgent,
    Raven,
    req,
}) {
    const options = {
        projectName: OPTIMIZELY_PROJECT,
        requestOriginUrl: url,
        userAgent,
        visitorId: sanitizeId(sessionId),
    };

    return async dispatch => {
        dispatch(loadingAssignments(options));

        const abClient = generateABClient({ fetch });

        try {
            const urlAssignments = {};

            if (req && req.query) {
                const isDevEnvironment = __DEV__ || !isProduction(req.hostname);
                // On DEV environments or preview.zocdoc.com, one may read experiments from URL parameters
                // Note: these will overwrite abClient experiments!
                if (isDevEnvironment) {
                    const { query } = req;
                    Object.keys(query).forEach(key => {
                        // Only set experiments if they exist in experiments.js!
                        if (experimentsKeyedById[key]) {
                            urlAssignments[key] = query[key];
                        }
                    });
                }
            }
            const response = await abClient.getProjectExperiments(options);
            const assignments = response.experiments.reduce((exp, it) => {
                // Don't override url parameters
                if (!exp[it.experimentId]) {
                    exp[it.experimentId] =
                        it.assignment === null
                            ? get(experimentsKeyedById, `${it.experimentId}.defaultAssignment`, it.assignment)
                            : it.assignment;
                }
                return exp;
            }, urlAssignments);
            dispatch(receivedAssignments(assignments));
        } catch (err) {
            log({
                logPrefix: 'fetchAssignments',
                message: 'Error fetching assignments from A/B service',
                error: err.stack,
            });
            dispatch(assignmentsError(err));
            if (Raven) {
                Raven.captureException(err);
            }
        }
    };
}

export function trackConversion({
    conversionId,
    fetch = fetchWithRetries,
    log = noop,
    sessionId,
    url,
    userAgent,
}) {
    const conversionsBody = {
        conversionData: {
            conversions: [{
                conversionId,
                visitorIdType: 'sessionId',
                sessionId,
            }],
            requestOriginUrl: url,
            userAgent,
            projectName: OPTIMIZELY_PROJECT,
        },
    };

    const abClient = generateABClient({ fetch });
    return async dispatch => {
        try {
            await abClient.trackConversion(conversionsBody);
            dispatch(recordedConversion(conversionId));
        } catch (err) {
            log({
                logPrefix: 'trackConversion',
                message: `Error tracking conversion for ${conversionId} from A/B service`,
                error: err.stack,
            });
            dispatch(conversionError(err));
        }
    };
}
